<template>
  <div class="dashboard-view">
    <section class="dashboard-grid">
      <router-link v-for="item in metrics" :key="item.key" :to="item.to" class="metric-card">
        <p>{{ item.label }}</p>
        <strong>{{ item.value }}</strong>
        <span>{{ item.description }}</span>
      </router-link>
    </section>

    <section class="dashboard-panel">
      <div class="dashboard-panel__head">
        <div>
          <h2>待处理 Pending</h2>
          <p>快速查看新询盘和新定制请求。</p>
        </div>
        <el-button size="small" :loading="loading" @click="loadDashboard">刷新 Refresh</el-button>
      </div>

      <div class="pending-grid" v-loading="loading">
        <router-link to="/inquiries" class="pending-card">
          <span>新询盘 New Inquiries</span>
          <strong>{{ pending.inquiries }}</strong>
        </router-link>
        <router-link to="/custom-requests" class="pending-card">
          <span>新定制 New Custom Requests</span>
          <strong>{{ pending.customRequests }}</strong>
        </router-link>
      </div>
    </section>

    <section class="dashboard-panel">
      <div class="dashboard-panel__head">
        <div>
          <h2>快捷操作 Quick Actions</h2>
          <p>常用后台入口和前台预览。</p>
        </div>
      </div>

      <div class="quick-grid">
        <router-link v-for="item in quickActions" :key="item.label" :to="item.to" class="quick-card">
          <strong>{{ item.label }}</strong>
          <span>{{ item.description }}</span>
        </router-link>
        <a class="quick-card" :href="webBaseUrl" target="_blank" rel="noopener noreferrer">
          <strong>查看前台 View Website</strong>
          <span>打开当前官网首页</span>
        </a>
      </div>
    </section>

    <section class="dashboard-columns">
      <article class="dashboard-panel">
        <div class="dashboard-panel__head">
          <div>
            <h2>最近询盘 Latest Inquiries</h2>
            <p>最近 5 条联系表单提交。</p>
          </div>
        </div>
        <el-table :data="latestInquiries" v-loading="loading" stripe>
          <el-table-column prop="name" label="客户 Customer" min-width="150" />
          <el-table-column prop="email" label="邮箱 Email" min-width="180" />
          <el-table-column prop="status" label="状态 Status" min-width="100" />
          <el-table-column prop="createdAt" label="时间 Created" min-width="160">
            <template slot-scope="{ row }">{{ formatDate(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </article>

      <article class="dashboard-panel">
        <div class="dashboard-panel__head">
          <div>
            <h2>最近定制 Latest Custom</h2>
            <p>最近 5 条定制请求。</p>
          </div>
        </div>
        <el-table :data="latestCustomRequests" v-loading="loading" stripe>
          <el-table-column prop="name" label="项目 Project" min-width="150" />
          <el-table-column prop="email" label="邮箱 Email" min-width="180" />
          <el-table-column prop="status" label="状态 Status" min-width="100" />
          <el-table-column prop="createdAt" label="时间 Created" min-width="160">
            <template slot-scope="{ row }">{{ formatDate(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </article>
    </section>
  </div>
</template>

<script>
import { fetchDashboardSummary } from '../api/dashboard'
import { getWebBaseUrl } from '../utils/preview'

export default {
  name: 'DashboardView',
  data () {
    return {
      loading: false,
      webBaseUrl: getWebBaseUrl(),
      counts: {
        products: 0,
        categories: 0,
        cases: 0,
        articles: 0,
        inquiries: 0,
        customRequests: 0,
        media: 0,
        pageSections: 0
      },
      pending: {
        inquiries: 0,
        customRequests: 0
      },
      latestInquiries: [],
      latestCustomRequests: []
    }
  },
  computed: {
    metrics () {
      return [
        { key: 'products', label: '产品 Products', value: this.counts.products, to: '/products', description: '产品条目和固定规格字段' },
        { key: 'categories', label: '分类 Categories', value: this.counts.categories, to: '/categories', description: '产品分类与颜色系列' },
        { key: 'cases', label: '案例 Cases', value: this.counts.cases, to: '/cases', description: '项目案例和图库' },
        { key: 'articles', label: '文章 Articles', value: this.counts.articles, to: '/articles', description: 'Knowledge 新闻内容' },
        { key: 'inquiries', label: '询盘 Inquiries', value: this.counts.inquiries, to: '/inquiries', description: '联系表单提交' },
        { key: 'customRequests', label: '定制请求 Custom Requests', value: this.counts.customRequests, to: '/custom-requests', description: 'Customize 页面请求' },
        { key: 'media', label: '媒体 Media', value: this.counts.media, to: '/media', description: '上传图片和文件' },
        { key: 'pageSections', label: '页面内容 Page Sections', value: this.counts.pageSections, to: '/page-sections', description: '页面描述和图片组' }
      ]
    },
    quickActions () {
      return [
        { label: '新增产品 Add Product', to: '/products', description: '进入产品列表创建新产品' },
        { label: '上传媒体 Upload Media', to: '/media', description: '进入媒体库上传图片' },
        { label: '编辑页面内容 Page Sections', to: '/page-sections', description: '维护官网页面文案和图片组' },
        { label: '处理询盘 Inquiries', to: '/inquiries', description: '查看和处理客户提交' }
      ]
    }
  },
  async created () {
    await this.loadDashboard()
  },
  methods: {
    async loadDashboard () {
      this.loading = true
      try {
        const { data } = await fetchDashboardSummary()
        this.counts = data.counts || this.counts
        this.pending = data.pending || this.pending
        this.latestInquiries = (data.latest && data.latest.inquiries ? data.latest.inquiries : []).map(item => ({
          name: [item.firstName, item.lastName].filter(Boolean).join(' ') || item.company || '-',
          email: item.email || '-',
          status: item.status,
          createdAt: item.createdAt
        }))
        this.latestCustomRequests = (data.latest && data.latest.customRequests ? data.latest.customRequests : []).map(item => ({
          name: item.craft || item.plankType || '-',
          email: item.email || '-',
          status: item.status,
          createdAt: item.createdAt
        }))
      } catch (error) {
        this.$message.error('仪表盘数据加载失败 Failed to load dashboard data.')
      } finally {
        this.loading = false
      }
    },
    formatDate (value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    }
  }
}
</script>

<style scoped>
.dashboard-grid,
.pending-grid,
.quick-grid,
.dashboard-columns {
  display: grid;
  gap: 18px;
}

.dashboard-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.pending-grid,
.quick-grid,
.dashboard-columns {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.metric-card,
.dashboard-panel,
.pending-card,
.quick-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(23, 20, 17, 0.04);
}

.metric-card,
.pending-card,
.quick-card {
  display: block;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.metric-card:hover,
.pending-card:hover,
.quick-card:hover {
  transform: translateY(-2px);
  border-color: rgba(23, 20, 17, 0.22);
}

.metric-card {
  min-height: 150px;
  padding: 22px;
}

.metric-card p {
  margin: 0 0 10px;
  color: var(--muted);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-card strong,
.pending-card strong {
  display: block;
  font-size: 34px;
  font-weight: 500;
}

.metric-card span,
.quick-card span {
  display: block;
  margin-top: 12px;
  color: #5b544d;
  line-height: 1.7;
}

.dashboard-panel {
  margin-top: 18px;
  padding: 24px;
}

.dashboard-columns .dashboard-panel {
  min-width: 0;
}

.dashboard-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.dashboard-panel h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
}

.dashboard-panel p {
  margin: 8px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.pending-card,
.quick-card {
  padding: 20px;
}

.pending-card span {
  color: var(--muted);
}

.quick-card strong {
  font-weight: 500;
}

@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .pending-grid,
  .quick-grid,
  .dashboard-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-panel__head {
    flex-direction: column;
  }
}
</style>
